import React, { useEffect, useState, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { getToken } from 'store/Auth';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { useNavigate } from 'react-router';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from 'config';
import { AuthRequest } from 'models/models';
import { Spinner } from 'components/atoms';

export const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useAppDispatch();

  const isAuth = useAppSelector(
    (state: RootState) => state.auth.user.isAuthenticated
  );

  // Memoize the login function to prevent it from changing on every render
  const aadLogin = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
    }
  }, [instance]);

  // Handle token acquisition when authenticated
  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      setIsLoading(true);
      const request = {
        ...loginRequest,
        account: accounts[0],
        forceRefresh: false // Use cached tokens if available
      };

      instance
        .acquireTokenSilent(request)
        .then((response) => {
          if (response.accessToken) {
            const query: AuthRequest = {
              token: response.accessToken
            };
            dispatch(getToken(query));
          }
        })
        .catch((error) => {
          console.error('Token acquisition error:', error);
          // If silent token acquisition fails, try interactive login
          instance
            .acquireTokenRedirect(request)
            .catch((interactiveError) => {
              console.error('Interactive token acquisition error:', interactiveError);
              setIsLoading(false);
            });
        });
    }
  }, [isAuthenticated, accounts, dispatch, instance]);

  // Navigate to home when authenticated
  useEffect(() => {
    if (isAuth) {
      setIsLoading(false);
      navigate('/');
    }
  }, [isAuth, navigate]);

  // Initiate login if not authenticated
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      aadLogin();
    }
  }, [isAuthenticated, inProgress, aadLogin]);

  return (
    <>
      {!isAuthenticated && inProgress !== InteractionStatus.None && (
        <Spinner showSpinner={true} loadingMessage={'Awaiting User Action..'} />
      )}
      {isLoading && (
        <Spinner
          showSpinner={true}
          loadingMessage={'Signing in.. Please wait..'}
        />
      )}
    </>
  );
};
